/**
 * Email template library + designed-email API contracts (messaging module, all plans; AI design
 * generation is Pro `ai.copy`). See docs/API.md "Email designs & templates".
 */
import { z } from 'zod';
import { EmailDesignSchema, MAX_EMAIL_HTML_INPUT } from '../emailDesign';
import { MESSAGE_TEMPLATE_KINDS } from './messaging';

const IsoDateTime = z.iso.datetime({ offset: true });

export const EMAIL_TEMPLATE_SOURCES = ['system', 'user', 'ai', 'html'] as const;
export type EmailTemplateSource = (typeof EMAIL_TEMPLATE_SOURCES)[number];

export const EmailTemplateSchema = z.object({
  id: z.uuid(),
  /** `system`: platform catalog (read-only, duplicate to edit); `tenant`: the business's own. */
  scope: z.enum(['system', 'tenant']),
  key: z.string().nullable(),
  name: z.string(),
  kind: z.enum(MESSAGE_TEMPLATE_KINDS),
  source: z.enum(EMAIL_TEMPLATE_SOURCES),
  locale: z.enum(['el', 'en']),
  subject: z.string().nullable(),
  design: EmailDesignSchema,
  createdBy: z.string().nullable(),
  archivedAt: IsoDateTime.nullable(),
  updatedAt: IsoDateTime,
});
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>;

export const EmailTemplateListQuerySchema = z.object({
  scope: z.enum(['all', 'system', 'tenant']).default('all'),
  archived: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
  q: z.string().max(100).optional(),
});

export const EmailTemplateListResponseSchema = z.object({ items: z.array(EmailTemplateSchema) });

export const CreateEmailTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  subject: z.string().max(200).nullable().optional(),
  kind: z.enum(MESSAGE_TEMPLATE_KINDS).default('custom'),
  /**
   * The AI design run that produced the design (from `POST /ai/design`). Provenance is decided by
   * the server: `ai` only when this run is a completed `email_design` run of the same user;
   * `html` for HTML designs; `user` otherwise. Clients cannot claim a source.
   */
  aiRunId: z.uuid().optional(),
  design: EmailDesignSchema,
});
export type CreateEmailTemplateRequest = z.input<typeof CreateEmailTemplateRequestSchema>;

export const UpdateEmailTemplateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    subject: z.string().max(200).nullable(),
    kind: z.enum(MESSAGE_TEMPLATE_KINDS),
    design: EmailDesignSchema,
  })
  .partial();
export type UpdateEmailTemplateRequest = z.input<typeof UpdateEmailTemplateRequestSchema>;

export const DuplicateEmailTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
});

// ─── sanitize / render ─────────────────────────────────────────────────────

export const SANITIZE_WARNING_CODES = [
  'removed_element',
  'removed_attribute',
  'blocked_url',
  'blocked_css',
  'removed_comment',
  'too_large',
  /** `{{…}}` that is not a plain merge variable (could span markup): neutralized. */
  'invalid_merge_token',
  /** Merge variables inside `<style>` are not supported: removed. */
  'merge_in_style',
] as const;

export const SanitizeWarningSchema = z.object({
  code: z.enum(SANITIZE_WARNING_CODES),
  /** Element/attribute/property name (e.g. `script`, `onerror`, `@import`). */
  detail: z.string(),
  count: z.number().int(),
});
export type SanitizeWarning = z.infer<typeof SanitizeWarningSchema>;

export const SanitizeHtmlRequestSchema = z.object({ html: z.string().max(MAX_EMAIL_HTML_INPUT) });
export const SanitizeHtmlResponseSchema = z.object({
  html: z.string(),
  bytes: z.number().int(),
  maxBytes: z.number().int(),
  warnings: z.array(SanitizeWarningSchema),
});
export type SanitizeHtmlResponse = z.infer<typeof SanitizeHtmlResponseSchema>;

export const RenderEmailRequestSchema = z.object({
  design: EmailDesignSchema,
  subject: z.string().max(200).optional(),
  contactId: z.uuid().optional(),
  locale: z.enum(['el', 'en']).optional(),
});
export type RenderEmailRequest = z.input<typeof RenderEmailRequestSchema>;

export const RenderEmailResponseSchema = z.object({
  subject: z.string(),
  html: z.string(),
  text: z.string(),
  bytes: z.number().int(),
  warnings: z.array(SanitizeWarningSchema),
  missingVariables: z.array(z.string()),
  unknownVariables: z.array(z.string()),
  issues: z.array(
    z.object({
      code: z.string(),
      blockId: z.string().optional(),
      severity: z.enum(['error', 'warning']),
    }),
  ),
});
export type RenderEmailResponse = z.infer<typeof RenderEmailResponseSchema>;

/** Business data the editor needs (brand colour, logo, name). */
export const EmailBrandSchema = z.object({
  businessName: z.string(),
  brandColor: z.string(),
  logoUrl: z.string().nullable(),
  category: z.string().nullable(),
  locale: z.enum(['el', 'en']),
});
export type EmailBrand = z.infer<typeof EmailBrandSchema>;

export const EmailImageUploadResponseSchema = z.object({
  fileId: z.uuid(),
  url: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
});
export type EmailImageUploadResponse = z.infer<typeof EmailImageUploadResponseSchema>;
export const EMAIL_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const EMAIL_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

// ─── AI design ("vibe coding", Pro `ai.copy`) ────────────────────────────────

export const AiEmailDesignRequestSchema = z.object({
  /** What the email should be ("καλοκαιρινή προσφορά -20% για κουρέματα…"). Untrusted. */
  prompt: z.string().trim().min(3).max(1500),
  /** Refinement: the current design the user wants changed. */
  current: EmailDesignSchema.optional(),
  /** Earlier instructions in this session, oldest first (refinements). */
  history: z.array(z.string().max(1500)).max(8).default([]),
  /** Client idempotency (one AI run per click). */
  requestId: z.string().min(8).max(100).optional(),
});
export type AiEmailDesignRequest = z.input<typeof AiEmailDesignRequestSchema>;

export const AiEmailDesignResponseSchema = z.object({
  design: EmailDesignSchema,
  subject: z.string(),
  /** One-line summary of what the model did (shown in the session log). */
  summary: z.string(),
  creditsUsed: z.number().int(),
  runId: z.uuid(),
  /** Links/images the model invented that the server removed (shown to the user). */
  removed: z.array(z.string()).default([]),
});
export type AiEmailDesignResponse = z.infer<typeof AiEmailDesignResponseSchema>;
